import React, { useRef, useEffect } from 'react';
import Comments from './Comments';

interface IsolatedCommentsWrapperProps {
  sessionId: string;
  token: string | null;
  isPublicView: boolean;
  isInline?: boolean;
  pendingTimestamp?: number | null;
  selectedCommentId?: string | null;
  onClose?: () => void;
  onCommentsChange?: (comments: any[]) => void;
  initialComments?: any[];      // Pre-loaded comments from CDN bundle
  disablePolling?: boolean;     // Disable polling for static/CDN content
}

// This component completely isolates Comments from any re-renders
// by using refs and only updating when specific props change
const IsolatedCommentsWrapper: React.FC<IsolatedCommentsWrapperProps> = (props) => {
  const propsRef = useRef(props);
  const [renderKey, setRenderKey] = React.useState(0);
  
  // Debug logging
  console.log('[IsolatedCommentsWrapper] Props received:', {
    sessionId: props.sessionId,
    token: props.token,
    isPublicView: props.isPublicView,
    isInline: props.isInline
  });
  
  // Only update the ref and trigger re-render when specific props change
  useEffect(() => {
    const hasRelevantChange = 
      propsRef.current.sessionId !== props.sessionId ||
      propsRef.current.token !== props.token ||
      propsRef.current.isPublicView !== props.isPublicView ||
      propsRef.current.pendingTimestamp !== props.pendingTimestamp ||
      propsRef.current.selectedCommentId !== props.selectedCommentId;
    
    if (hasRelevantChange) {
      propsRef.current = props;
      setRenderKey(prev => prev + 1);
    }
  }, [props.sessionId, props.token, props.isPublicView, props.pendingTimestamp, props.selectedCommentId]);
  
  // Store callbacks in refs to prevent re-renders
  const onCloseRef = useRef(props.onClose);
  const onCommentsChangeRef = useRef(props.onCommentsChange);
  
  useEffect(() => {
    onCloseRef.current = props.onClose;
    onCommentsChangeRef.current = props.onCommentsChange;
  });
  
  return (
    <Comments
      key={renderKey}
      sessionId={propsRef.current.sessionId}
      token={propsRef.current.token}
      isPublicView={propsRef.current.isPublicView}
      isInline={propsRef.current.isInline}
      pendingTimestamp={propsRef.current.pendingTimestamp}
      selectedCommentId={propsRef.current.selectedCommentId}
      onClose={() => onCloseRef.current?.()}
      onCommentsChange={(comments) => onCommentsChangeRef.current?.(comments)}
      initialComments={propsRef.current.initialComments}
      disablePolling={propsRef.current.disablePolling}
    />
  );
};

export default React.memo(IsolatedCommentsWrapper);